import type { DayRange } from './day';
import { entriesIn, type JournalEntry } from './journal';

/**
 * Profit and loss over a period, computed from the ledger.
 *
 * Two things here are easy to get wrong and expensive when wrong, so they are
 * stated explicitly rather than left to the reader:
 *
 * 1. **Purchases are not an expense.** Goods bought become stock, and are
 *    expensed as cost of goods when they sell. Counting a delivery as a cost
 *    would show a loss on any month the shop restocked. `purchases` is reported
 *    separately so the screen can say so.
 *
 * 2. **Profit is not cash.** A credit sale is revenue the day it happens, but no
 *    money arrives until the debt is collected. Both figures are returned, and
 *    the screen shows them side by side, because shopkeepers conflate them —
 *    showing only profit produces "where is my money?", showing only cash
 *    produces "why was a big collection month so profitable?".
 *
 * Mirror in the back office as a `ProfitAndLoss` response shape.
 */
export interface ProfitAndLoss {
  /** Accrual: a credit sale counts on the day it was sold. */
  revenue: number;
  /** Cost of the goods sold, snapshotted per sale when it happened. */
  cogs: number;
  grossProfit: number;
  /** Expenses net of reversals. Excludes purchases. */
  expenses: number;
  netProfit: number;

  /** Goods bought in the period. Reported, never counted as an expense. */
  purchases: number;

  /** Cash sales plus debt collected. */
  cashIn: number;
  /** Cash expenses plus cash purchases. */
  cashOut: number;
  cashNet: number;

  /**
   * The share of revenue whose products had a cost recorded, 0–1.
   *
   * Without this the profit reads as authoritative while overstating the margin
   * on every uncosted item. Below about 0.9 the screen must say so.
   */
  coverage: number;

  bills: number;
}

export function profitAndLoss(journal: JournalEntry[], range: DayRange): ProfitAndLoss {
  let revenue = 0;
  let cogs = 0;
  let costedRevenue = 0;
  let expenses = 0;
  let purchases = 0;
  let cashIn = 0;
  let cashOut = 0;
  let bills = 0;

  for (const entry of entriesIn(journal, range)) {
    switch (entry.kind) {
      case 'sale':
        revenue += entry.total;
        cogs += entry.cogs;
        costedRevenue += entry.costedRevenue;
        bills += 1;
        if (entry.payMode === 'cash') cashIn += entry.total;
        break;

      case 'payment':
        // Collecting a debt is cash, not revenue: the sale was already counted
        // on the day it happened. Counting it again would double the month.
        cashIn += entry.amount;
        break;

      case 'expense':
        expenses += entry.amount;
        if (entry.cash) cashOut += entry.amount;
        break;

      case 'purchase':
        purchases += entry.total;
        if (entry.cash) cashOut += entry.total;
        break;

      default:
        break;
    }
  }

  const grossProfit = revenue - cogs;

  return {
    revenue,
    cogs,
    grossProfit,
    expenses,
    netProfit: grossProfit - expenses,
    purchases,
    cashIn,
    cashOut,
    cashNet: cashIn - cashOut,
    coverage: revenue > 0 ? costedRevenue / revenue : 0,
    bills,
  };
}

/** Below this, the profit figure is understating cost enough to mislead. */
export const COVERAGE_WARN = 0.9;
